import type {
  CarouselPresentation,
  ChoicePresentation,
  DatePickerPresentation,
  InteractionCapability,
  InteractionPresentation,
  InteractionResponse,
} from './types';

const PRESENTATION_BYTES = 65_536;
const RESPONSE_TEXT_CODE_POINTS = 2_000;
const RESPONSE_TEXT_BYTES = 8_192;
const URL_CHARACTERS = 2_048;
const IDENTIFIER = /^[A-Za-z0-9_-]{1,64}$/;

const object = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);
const only = (value: Record<string, unknown>, keys: readonly string[]) =>
  Object.keys(value).every((key) => keys.includes(key));
const codePoints = (value: string) => [...value].length;
const bytes = (value: string) => new TextEncoder().encode(value).length;
const hasDisallowedControl = (value: string) =>
  [...value].some((character) => {
    const code = character.codePointAt(0) ?? 0;
    return code === 127 || (code < 32 && code !== 9 && code !== 10 && code !== 13);
  });
const hasUrlWhitespaceOrControl = (value: string) =>
  [...value].some((character) => {
    const code = character.codePointAt(0) ?? 0;
    return code <= 32 || code === 127;
  });
const text = (value: unknown, maximum: number, required = false): value is string =>
  typeof value === 'string' && codePoints(value) <= maximum && !hasDisallowedControl(value) &&
  (!required || value.trim().length > 0);
const canonical = (value: unknown): unknown =>
  Array.isArray(value) ? value.map(canonical) : object(value)
    ? Object.fromEntries(Object.keys(value).sort().map((key) => [key, canonical(value[key])])) : value;
const withinPresentationBudget = (value: unknown) => {
  try {
    return bytes(JSON.stringify(canonical(value))) <= PRESENTATION_BYTES;
  } catch {
    return false;
  }
};

// Tool carousel images are keyed by interaction uuid; welcome images by agent id (`agent_<base64url>`, not a uuid).
const PROXY_IMAGE_PATH = /^\/v1\/public\/chat\/images\/(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|welcome\/[A-Za-z0-9_-]{1,80})\/(?:0|[1-9][0-9]*)$/i;

export function isProxyImageUrl(value: unknown): value is string {
  return typeof value === 'string' && PROXY_IMAGE_PATH.test(value);
}

export function isPublicHttpsUrl(value: unknown): value is string {
  if (typeof value !== 'string' || value.length === 0 || value.length > URL_CHARACTERS || hasUrlWhitespaceOrControl(value)) return false;
  try {
    const url = new URL(value);
    const hostname = url.hostname.toLowerCase();
    if (url.protocol !== 'https:' || url.username || url.password || url.hash || url.port) return false;
    if (!hostname || hostname === 'localhost' || hostname.endsWith('.localhost') || hostname.endsWith('.local') || hostname.endsWith('.internal')) return false;
    if (hostname.startsWith('[') || /^\d{1,3}(?:\.\d{1,3}){3}$/.test(hostname)) return false;
    return url.toString().length <= URL_CHARACTERS;
  } catch {
    return false;
  }
}

export function isChoicePresentation(value: unknown, maximum?: number): value is ChoicePresentation {
  if (!object(value) || !only(value, ['label', 'description', 'options']) || !text(value.label, 160, true) ||
      ('description' in value && value.description !== undefined && !text(value.description, 240)) || !Array.isArray(value.options) || value.options.length === 0 ||
      (maximum !== undefined && value.options.length > maximum)) return false;
  const ids = new Set<string>();
  let typedOptions = 0;
  for (const option of value.options) {
    if (!object(option) || !only(option, ['id', 'label', 'description', 'allow_input', 'input_placeholder']) ||
        typeof option.id !== 'string' || !IDENTIFIER.test(option.id) || ids.has(option.id) || !text(option.label, 80, true) ||
        ('description' in option && option.description !== undefined && !text(option.description, 240))) return false;
    const allowsInput = option.allow_input === true;
    if ('allow_input' in option && !allowsInput) return false;
    if ('input_placeholder' in option && (!allowsInput || !text(option.input_placeholder, 160, true))) return false;
    if (allowsInput) typedOptions += 1;
    if (typedOptions > 1) return false;
    ids.add(option.id);
  }
  return withinPresentationBudget(value);
}

export function isButtonsV1Presentation(value: unknown, maximum?: number): value is ChoicePresentation {
  return isChoicePresentation(value, maximum) && value.options.every((option) =>
    !Object.prototype.hasOwnProperty.call(option, 'allow_input') &&
    !Object.prototype.hasOwnProperty.call(option, 'input_placeholder'));
}

export function isCarouselPresentation(value: unknown, maximum?: number, allowProxy = false): value is CarouselPresentation {
  if (!object(value) || !only(value, ['label', 'description', 'items']) || !text(value.label, 160, true) ||
      ('description' in value && value.description !== undefined && !text(value.description, 240)) || !Array.isArray(value.items) || value.items.length === 0 ||
      (maximum !== undefined && value.items.length > maximum)) return false;
  const ids = new Set<string>();
  for (const item of value.items) {
    if (!object(item) || !only(item, ['id', 'title', 'description', 'image', 'action_label']) ||
        typeof item.id !== 'string' || !IDENTIFIER.test(item.id) || ids.has(item.id) || !text(item.title, 120, true) ||
        ('description' in item && item.description !== undefined && !text(item.description, 500)) ||
        ('action_label' in item && item.action_label !== undefined && !text(item.action_label, 80, true))) return false;
    if ('image' in item && item.image !== undefined) {
      if (!object(item.image) || !only(item.image, ['url', 'alt']) ||
          !(isPublicHttpsUrl(item.image.url) || (allowProxy && isProxyImageUrl(item.image.url))) || !text(item.image.alt, 240, true)) return false;
    }
    ids.add(item.id);
  }
  return withinPresentationBudget(value);
}

export function isCalendarDate(value: unknown): value is string {
  if (typeof value !== 'string' || !/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(value)) return false;
  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(5, 7));
  const day = Number(value.slice(8, 10));
  if (year < 1 || year > 9999 || month < 1 || month > 12 || day < 1) return false;
  const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const monthDays = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return day <= monthDays[month - 1]!;
}

export function isDatePickerPresentation(value: unknown): value is DatePickerPresentation {
  if (!object(value) || !only(value, ['label', 'description', 'min_date', 'max_date', 'confirm_label', 'selection_mode']) || !text(value.label, 160, true) ||
      !['single', 'range'].includes(String(value.selection_mode)) ||
      ('description' in value && value.description !== undefined && !text(value.description, 240)) ||
      ('confirm_label' in value && value.confirm_label !== undefined && !text(value.confirm_label, 80, true)) ||
      ('min_date' in value && value.min_date !== undefined && !isCalendarDate(value.min_date)) ||
      ('max_date' in value && value.max_date !== undefined && !isCalendarDate(value.max_date))) return false;
  if (typeof value.min_date === 'string' && typeof value.max_date === 'string' && value.min_date > value.max_date) return false;
  return withinPresentationBudget(value);
}

export function isInteractionPresentation(pair: InteractionCapability, value: unknown): value is InteractionPresentation {
  if (pair.tool_type === 'ui_buttons' && pair.schema_version === 1) return isButtonsV1Presentation(value);
  if (pair.tool_type === 'ui_buttons' && pair.schema_version === 2) return isChoicePresentation(value);
  if (pair.tool_type === 'ui_carousel' && pair.schema_version === 1) return isCarouselPresentation(value, undefined, true);
  if (pair.tool_type === 'ui_date_picker' && pair.schema_version === 1) return isDatePickerPresentation(value);
  return false;
}

export function isInteractionResponse(pair: InteractionCapability, value: unknown): value is InteractionResponse {
  if (!object(value) || !only(value, ['kind', 'value']) || Object.keys(value).length !== 2) return false;
  if (value.kind === 'date_range') {
    return pair.tool_type === 'ui_date_picker' && pair.schema_version === 1 && object(value.value) &&
      only(value.value, ['start_date', 'end_date']) && Object.keys(value.value).length === 2 &&
      isCalendarDate(value.value.start_date) && isCalendarDate(value.value.end_date) &&
      value.value.start_date < value.value.end_date;
  }
  if (typeof value.value !== 'string') return false;
  if (value.kind === 'selection') return (pair.tool_type === 'ui_buttons' || pair.tool_type === 'ui_carousel') && IDENTIFIER.test(value.value);
  if (value.kind === 'text') return pair.tool_type === 'ui_buttons' && pair.schema_version === 2 && value.value === value.value.trim() &&
    text(value.value, RESPONSE_TEXT_CODE_POINTS, true) && bytes(value.value) <= RESPONSE_TEXT_BYTES;
  if (value.kind === 'date') return pair.tool_type === 'ui_date_picker' && pair.schema_version === 1 && isCalendarDate(value.value);
  return false;
}

